import { ArrowDownward, ArrowUpward, Delete, Edit, Image as ImageIcon, Link as LinkIcon, MoreVert } from "@mui/icons-material";
import { Avatar, Box, Button, Card, CardActions, CardContent, CardHeader, CardMedia, Chip, FormControlLabel, Grid, Icon, IconButton, Menu, MenuItem, Pagination, Paper, Switch, Typography, useTheme } from "@mui/material";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { format } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContent } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { useBannerContext } from "../../../shared/contexts/BannerContext";
import { Environment } from "../../../shared/environment";
import { useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { BannerService, IBanner } from "../../../shared/services/api/banners/BannerService";
import {
    globalBackgroundPeople,
    globalColorPeople,
} from "../../../shared/utils/global-changeColors";
import { BannerModal } from "./BannerModal";

export const BannerManagementPresentation: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const [rows, setRows] = useState<IBanner[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedBannerId, setSelectedBannerId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalBannerId, setModalBannerId] = useState<string | null>(null);

    const pagina = useMemo(() => {
        return Number(searchParams.get("pagina") || "1");
    }, [searchParams]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setSearchParams({ pagina: newPage.toString() }, { replace: true });
    };

    const toastHandler = useToast();

    const fetchData = () => {
        setIsLoading(true);
        BannerService.getAll(pagina).then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
                toastHandler.present({
                    type: "error",
                    messageOrContent: 'Erro ao listar banners.',
                });
            } else {
                const sortedData = result.data.sort((a, b) => a.order - b.order);
                setRows(sortedData);
                setTotalCount(result.totalCount);
            }
        });
    };

    useEffect(() => {
        fetchData();
    }, [pagina]);

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
        setAnchorEl(event.currentTarget);
        setSelectedBannerId(id);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedBannerId(null);
    };

    const handleDelete = async () => {
        handleCloseMenu();
        if (selectedBannerId && confirm("Tem certeza que deseja excluir este banner?")) {
            await BannerService.deleteById(selectedBannerId);
            setRows((oldRows) => [
                ...oldRows.filter((oldRow) => oldRow.id !== selectedBannerId),
            ]);
            toastHandler.present({
                type: "success",
                messageOrContent: <ToastContent title="Sucesso" content="Banner excluído com sucesso!" />,
            });
        }
    };

    const handleEdit = () => {
        handleCloseMenu();
        if (selectedBannerId) {
            setModalBannerId(selectedBannerId);
            setIsModalOpen(true);
        }
    };

    const handleAdd = () => {
        setModalBannerId(null);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setModalBannerId(null);
    };

    const { showBanners, toggleShowBanners, refreshBanners } = useBannerContext();

    const canAdd = checkByProfileGuard(PermissionRolesEnum.BannerOptions, PermissionActionsEnum.Add);
    const canUpdate = checkByProfileGuard(PermissionRolesEnum.BannerOptions, PermissionActionsEnum.Update);

    const handleModalSave = () => {
        fetchData();
        refreshBanners();
    };

    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const handleReorder = async (fromIndex: number, toIndex: number) => {
        if (fromIndex === toIndex) return;

        const newRows = [...rows];
        const [movedItem] = newRows.splice(fromIndex, 1);
        newRows.splice(toIndex, 0, movedItem);

        // Update order locally
        newRows.forEach((row, idx) => row.order = idx + 1);
        setRows(newRows);

        // Persist changes to backend
        // We only need to update the items that changed position or order index.
        // For simplicity and robustness in this list size, we can update all affected items.
        // The affected items are those between fromIndex and toIndex (inclusive).

        const start = Math.min(fromIndex, toIndex);
        const end = Math.max(fromIndex, toIndex);

        const updates = [];
        for (let i = start; i <= end; i++) {
            const row = newRows[i];
            updates.push(BannerService.updateOrder(row.id, row.order));
        }

        try {
            await Promise.all(updates);
            toastHandler.present({
                type: "success",
                messageOrContent: "Ordem atualizada com sucesso!",
            });
        } catch (error) {
            toastHandler.present({
                type: "error",
                messageOrContent: "Erro ao salvar a nova ordem.",
            });
            // Revert local state if needed, but for now we keep it as user might try again
            // or we could fetch data again to sync with server
            fetchData();
        }
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault(); // Necessary to allow dropping
    };

    const handleDrop = (index: number) => {
        if (draggedIndex === null) return;
        handleReorder(draggedIndex, index);
        setDraggedIndex(null);
    };

    const handleMove = (index: number, direction: 'up' | 'down') => {
        const toIndex = direction === 'up' ? index - 1 : index + 1;
        if (toIndex >= 0 && toIndex < rows.length) {
            handleReorder(index, toIndex);
        }
    };

    return (
        <LayoutBaseDePagina
            titulo="Gestão de Banners"
            barraDeFerramentas={
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} component={Paper} padding={1} paddingX={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showBanners}
                                onChange={toggleShowBanners}
                                color="primary"
                                disabled={!canUpdate}
                            />
                        }
                        label="Exibir Banners no Dashboard"
                    />
                    {canAdd && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAdd}
                            startIcon={<Icon>add</Icon>}
                        >
                            Novo Banner
                        </Button>
                    )}
                </Box>
            }
        >
            <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" padding={2} sx={{ backgroundColor: 'transparent', border: 'none' }}>


                {rows.length === 0 && !isLoading && (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                        <Typography variant="h6" color="textSecondary">
                            Nenhum banner cadastrado.
                        </Typography>
                    </Box>
                )}

                <Grid container spacing={3}>
                    {rows.map((banner, index) => (
                        <Grid
                            item
                            xs={12} sm={6} md={4} lg={3}
                            key={banner.id}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={handleDragOver}
                            onDrop={() => handleDrop(index)}
                            sx={{
                                opacity: draggedIndex === index ? 0.5 : 1,
                                cursor: 'grab',
                                transition: 'opacity 0.2s',
                            }}
                        >
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                    }
                                }}
                            >
                                <CardHeader
                                    avatar={
                                        <Avatar sx={{ bgcolor: theme.palette.primary.main }} aria-label="recipe">
                                            <ImageIcon />
                                        </Avatar>
                                    }
                                    action={
                                        <IconButton aria-label="settings" onClick={(e) => handleOpenMenu(e, banner.id)}>
                                            <MoreVert />
                                        </IconButton>
                                    }
                                    title={
                                        <Typography variant="subtitle1" fontWeight="bold" noWrap title={banner.title}>
                                            {banner.title}
                                        </Typography>
                                    }
                                    subheader={
                                        <Typography variant="caption" color="text.secondary">
                                            {banner.createdAt ? format(new Date(banner.createdAt), 'dd/MM/yyyy') : 'Data desconhecida'}
                                        </Typography>
                                    }
                                />
                                <CardMedia
                                    component="img"
                                    height="160"
                                    image={banner.image || banner.imageUrl}
                                    alt={banner.title}
                                    sx={{ objectFit: 'contain', backgroundColor: theme.palette.grey[100] }}
                                    onError={(e: any) => {
                                        e.target.src = 'https://via.placeholder.com/800x400?text=Imagem+Indisponivel';
                                    }}
                                />
                                <CardContent sx={{ flexGrow: 1, paddingBottom: 1 }}>
                                    <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                                        <Chip
                                            label={banner.active ? "Ativo" : "Inativo"}
                                            size="small"
                                            sx={{
                                                color: globalColorPeople(banner.active ? "active" : "inactive"),
                                                fontWeight: 500,
                                                background: globalBackgroundPeople(banner.active ? "active" : "inactive"),
                                                border: "0.5px solid #f1f1f1",
                                            }}
                                        />
                                        <Chip
                                            label={`Ordem: ${banner.order}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ borderColor: theme.palette.grey[300] }}
                                        />
                                    </Box>
                                    {banner.link && (
                                        <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                                            <LinkIcon fontSize="small" sx={{ fontSize: 16 }} />
                                            <Typography variant="caption" noWrap title={banner.link}>
                                                {banner.link}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                                <CardActions disableSpacing sx={{ justifyContent: 'space-between', borderTop: `1px solid ${theme.palette.divider}` }}>
                                    <Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMove(index, 'up')}
                                            disabled={index === 0}
                                            title="Mover para cima"
                                        >
                                            <ArrowUpward fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMove(index, 'down')}
                                            disabled={index === rows.length - 1}
                                            title="Mover para baixo"
                                        >
                                            <ArrowDownward fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    PaperProps={{
                        elevation: 3,
                        sx: { minWidth: 150 }
                    }}
                >
                    {canUpdate && (
                        <MenuItem onClick={handleEdit}>
                            <Edit fontSize="small" sx={{ marginRight: 1, color: theme.palette.text.secondary }} />
                            Editar
                        </MenuItem>
                    )}
                    {canUpdate && (
                        <MenuItem onClick={handleDelete} sx={{ color: theme.palette.error.main }}>
                            <Delete fontSize="small" sx={{ marginRight: 1 }} />
                            Excluir
                        </MenuItem>
                    )}
                </Menu>

                {(totalCount > 0 && totalCount > Environment.LIMITE_LINHAS) && (
                    <Box display="flex" justifyContent="center" marginTop={2}>
                        <Pagination
                            page={pagina}
                            count={Math.ceil(totalCount / Environment.LIMITE_LINHAS)}
                            onChange={handlePageChange}
                        />
                    </Box>
                )}

                <BannerModal
                    open={isModalOpen}
                    onClose={handleModalClose}
                    onSave={handleModalSave}
                    id={modalBannerId}
                    initialImage={rows.find(r => r.id === modalBannerId)?.image || rows.find(r => r.id === modalBannerId)?.imageUrl}
                />
            </Box>

            <LoadingComp isopen={isLoading} />
        </LayoutBaseDePagina>
    );
};
